import React, {useState, useEffect, useRef} from 'react';
import {Button, Modal, message, Upload, Checkbox, Space, Spin} from 'antd';
import {UploadOutlined} from '@ant-design/icons';
import {useMutation} from '@apollo/client';
import {ZipcodeColumns} from './components/ZipcodeColumns';
import ZipcodeFilter from './components/ZipcodeFilter';
import {ZipcodeFiltersType} from "./graphql/queries/getZipcodeListing";
import {useZipcodeQuery} from "./hooks/useZipcodeQuery";
import TablePaginationBar from '../../../components/TablePaginationBar';
import { 
    EXPORT_ZIPCODES, 
    IMPORT_ZIPCODES_BY_OBJECT_KEY, 
    ImportZipcodesByObjectKeyData,
    ImportZipcodesByObjectKeyVariables,
    BATCH_UPDATE_ALLOW_TAKING_ORDER,
    BatchUpdateAllowTakingOrderData,
    BatchUpdateAllowTakingOrderVariables,
    CREATE_ZIPCODE_EXPORT_TASK,
    CreateZipcodeExportTaskData,
    CreateZipcodeExportTaskVariables
} from "../../../graphql/mutations/zipcodeMutations";
import { useNavigate } from 'react-router-dom';
import { fileUpload } from '../../../utils/storageUtils';
import { useTranslation } from 'react-i18next';
import { isHeadquartersAdmin } from '../../../utils/auth';


const ZipcodeInitialValue: ZipcodeFiltersType = {};

const PageBody: React.FC = () => {
    const [filters, setFilters] = useState<ZipcodeFiltersType>(ZipcodeInitialValue);
    const [tempFilters, setTempFilters] = useState<ZipcodeFiltersType>(ZipcodeInitialValue);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [importing, setImporting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [importingGlobal, setImportingGlobal] = useState(false);
    const [exportCompleted, setExportCompleted] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [pendingAction, setPendingAction] = useState<'allow' | 'restrict' | null>(null);
    const [pageExportLoading, setPageExportLoading] = useState(false);
    const {t} = useTranslation();
    const navigate = useNavigate();
    // 使用自定义hook获取数据
    const {
        zipcodeData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useZipcodeQuery(filters, currentPage, pageSize);

    // 添加GraphQL导出mutation（用于导入对话框中的模板导出）
    const [exportZipcodes, { loading: exportLoading }] = useMutation<any, any>(EXPORT_ZIPCODES);

    // 添加GraphQL导入mutation
    const [importZipcodesByObjectKey, { loading: importByObjectKeyLoading }] = useMutation<ImportZipcodesByObjectKeyData, ImportZipcodesByObjectKeyVariables>(IMPORT_ZIPCODES_BY_OBJECT_KEY);

    // 添加GraphQL批量更新mutation
    const [batchUpdateAllowTakingOrder, { loading: batchUpdateLoading }] = useMutation<BatchUpdateAllowTakingOrderData, BatchUpdateAllowTakingOrderVariables>(BATCH_UPDATE_ALLOW_TAKING_ORDER);
    
    // 添加GraphQL导出任务创建mutation
    const [createZipcodeExportTask] = useMutation<CreateZipcodeExportTaskData, CreateZipcodeExportTaskVariables>(CREATE_ZIPCODE_EXPORT_TASK);

    const handleResetFilters = () => {
        setTempFilters(ZipcodeInitialValue);
        setFilters(ZipcodeInitialValue);
        setSelectedRowKeys([]);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    // 添加useEffect监听状态变化后执行查询
    useEffect(() => {
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize]);

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) {
            setPageSize(size);
        }
    };

    // 导出逻辑
    const handleExport = async () => {
        const exportStartTime = Date.now();
        setExporting(true);
        try {
            const result = await exportZipcodes({
                variables: {
                    input: {
                        zipcode:  undefined,
                        city: undefined,
                        state:  undefined,
                        isDelivery: undefined,
                        isPickup: undefined,
                        format: "xlsx"
                    }
                }
            });

            if (result.data?.exportZipcodes.success) {
                const exportData = result.data.exportZipcodes;
                const downloadUrl = exportData.downloadUrl;
                if (downloadUrl) {
                    const response = await fetch(downloadUrl);
                    const blob = await response.blob();
                    const now = new Date();
                    const downloadFilename = `zipcodes_export_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}.xlsx`;
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = downloadFilename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);

                    const totalTimeMs = Math.round((Date.now() - exportStartTime) / 1000);
                    let successMessage = 'Export completed successfully. File download started.';
                    successMessage += ` Time taken: ${totalTimeMs} seconds`;
                    if (exportData.recordCount !== undefined) {
                        successMessage += `, Records exported: ${exportData.recordCount}`;
                    }
                    message.success(successMessage);
                    setExportCompleted(true);
                }
            } else {
                message.error(result.data?.exportZipcodes.message || 'Export failed');
            }
        } catch (error) {
            console.error('Export error:', error);
            message.error('Export failed');
        } finally {
            setExporting(false);
        }
    };

    // 唤起文件选择
    const handleContinueImport = () => {
        setImportModalVisible(false);
        document.getElementById('zipcode-import-input')?.click();
    };

    // 打开导入弹框时重置导出完成状态
    const handleOpenImportModal = () => {
        setImportModalVisible(true);
        setExportCompleted(false);
    };

    // 文件选择后自动上传并导入
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // 验证文件类型
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            message.error('Please select an Excel file (.xlsx or .xls)');
            e.target.value = '';
            return;
        }
        
        const importStartTime = Date.now();
        setImportingGlobal(true);
        try {
            // 1. 上传文件到对象存储，获取objectKey
            const { objectKey } = await fileUpload(file);

            // 2. 调用后端导入接口
            const { data: importData } = await importZipcodesByObjectKey({
                variables: {
                    objectKey,
                    fileName: file.name
                }
            });

            if (importData?.importZipcodesByObjectKey.success) {
                const result = importData.importZipcodesByObjectKey;
                const totalTimeMs = Math.round((Date.now() - importStartTime) / 1000);
                const imported = result.importedRecords ?? 0;
                message.success(`Imported ${imported} records in ${totalTimeMs} seconds`);
                refetch(buildQueryVariables());
                setImportModalVisible(false);
            } else {
                message.error(importData?.importZipcodesByObjectKey.message || 'Import failed');
            }
        } catch (error) {
            console.error('Import error:', error);
            message.error('Import failed');
        } finally {
            setImportingGlobal(false);
            e.target.value = '';
        }
    };

    // 批量更新允许接单状态
    const handleBatchUpdateAllowTakingOrder = async (allowTakingOrder: boolean) => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('central.zipcodes.messages.pleaseSelectZipcodes'));
            return;
        }

        try {
            const result = await batchUpdateAllowTakingOrder({
                variables: {
                    zipcodes: selectedRowKeys.map(key => key.toString()),
                    allowTakingOrder
                }
            });

            if (result.data?.batchUpdateAllowTakingOrder.success) {
                const successMsg = result.data.batchUpdateAllowTakingOrder.message;
                const updatedCount = result.data.batchUpdateAllowTakingOrder.updatedCount;
                // 尝试翻译，如果翻译不到则显示原消息
                const translatedMsg = t(successMsg as any, { count: updatedCount });
                message.success(translatedMsg === successMsg ? successMsg : translatedMsg);
                setSelectedRowKeys([]);
                refetch(buildQueryVariables());
            } else {
                const errorMsg = result.data?.batchUpdateAllowTakingOrder.message || 'central.zipcodes.messages.operationFailed';
                // 尝试翻译，如果翻译不到则显示原消息
                const translatedMsg = t(errorMsg as any);
                message.error(translatedMsg === errorMsg ? errorMsg : translatedMsg);
            }
        } catch (error: any) {
            console.error('Batch update error:', error);
            const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || t('central.zipcodes.messages.operationFailed');
            message.error(errorMessage);
        }
    };

    // 处理允许接单按钮点击
    const handleAllowTakingOrder = () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('central.zipcodes.messages.pleaseSelectZipcodes'));
            return;
        }
        setPendingAction('allow');
        setConfirmModalVisible(true);
    };

    // 处理限制接单按钮点击
    const handleRestrictTakingOrder = () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('central.zipcodes.messages.pleaseSelectZipcodes'));
            return;
        }
        setPendingAction('restrict');
        setConfirmModalVisible(true);
    };

    // 确认对话框确认
    const handleConfirm = () => {
        if (pendingAction === 'allow') {
            handleBatchUpdateAllowTakingOrder(true);
        } else if (pendingAction === 'restrict') {
            handleBatchUpdateAllowTakingOrder(false);
        }
        setConfirmModalVisible(false);
        setPendingAction(null);
    };
    
    // 页面导出功能
    const handlePageExport = async () => {
        try {
            setPageExportLoading(true);
            const result = await createZipcodeExportTask({
                variables: {
                    zipcodes: selectedRowKeys.length > 0 
                        ? selectedRowKeys.map(key => key.toString())
                        : null,
                    filters: selectedRowKeys.length > 0 ? null : filters
                }
            });

            if (result.data?.createZipcodeExportTask.success) {
                message.success(t('central.zipcodes.messages.exportTaskCreated') || '导出任务已创建，正在跳转到下载页面...');
                // 跳转到下载页面
                navigate('/central/download');
            } else {
                message.error(result.data?.createZipcodeExportTask.message || '创建导出任务失败');
            }
        } catch (error) {
            console.error('Export error:', error);
            message.error('创建导出任务失败');
        } finally {
            setPageExportLoading(false);
        }
    };

    // 确认对话框取消
    const handleCancel = () => {
        setConfirmModalVisible(false);
        setPendingAction(null);
    };

    // 表格行选择配置
    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys: React.Key[]) => {
            setSelectedRowKeys(selectedKeys);
        },
    };

    return (
        <Spin spinning={exporting || importingGlobal} tip={exporting ? 'Exporting...' : 'Importing...'} size="large">
            <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* 筛选条件 */}
                <ZipcodeFilter
                    filters={tempFilters}
                    setFilters={setTempFilters}
                />

                {/* 操作按钮 */}
                {/* 操作按钮（同一行：筛选/重置在“允许接单”左侧） */}
                <div className="mb-2 flex justify-between items-center gap-4 pl-0">
                    <div className="flex items-center gap-2">
                        {isHeadquartersAdmin() && (
                            <>
                                <Button
                                    type="primary"
                                    onClick={handleOpenImportModal}
                                    disabled={exporting || importingGlobal || loading}
                                    className="w-auto min-w-24"
                                >
                                    <span className="font-bold">{t('central.zipcodes.filters.import')}</span>
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handlePageExport}
                                    disabled={exporting || importingGlobal || loading}
                                    loading={pageExportLoading}
                                    className="w-auto min-w-24"
                                >
                                    <span className="font-bold">{t('central.zipcodes.actions.export')}</span>
                                </Button>
                            </>
                        )}
                    </div>

                    <div className="flex items-center gap-2 flex-nowrap">
                        <Button
                            type="primary"
                            onClick={handleFilter}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('central.zipcodes.filters.filter')}</span>
                        </Button>
                        <Button
                            onClick={handleResetFilters}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{t('central.zipcodes.filters.reset')}</span>
                        </Button>

                        {isHeadquartersAdmin() && (
                            <>
                                <Button
                                    type="primary"
                                    onClick={handleAllowTakingOrder}
                                    disabled={exporting || importingGlobal || loading || selectedRowKeys.length === 0 || batchUpdateLoading}
                                    loading={batchUpdateLoading && pendingAction === 'allow'}
                                    className="w-auto min-w-24"
                                >
                                    <span className="font-bold">{t('central.zipcodes.actions.allowTakingOrder')}</span>
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handleRestrictTakingOrder}
                                    disabled={exporting || importingGlobal || loading || selectedRowKeys.length === 0 || batchUpdateLoading}
                                    loading={batchUpdateLoading && pendingAction === 'restrict'}
                                    className="w-auto min-w-24"
                                >
                                    <span className="font-bold">{t('central.zipcodes.actions.restrictTakingOrder')}</span>
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* 导入弹窗 */}
                <Modal
                    open={importModalVisible}
                    title={<span className="text-red-500 font-bold">{t('central.zipcodes.filters.importModel')}</span>}
                    onCancel={() => setImportModalVisible(false)}
                    footer={null}
                    maskClosable={false}
                    width={600}
                    confirmLoading={importingGlobal}
                    okButtonProps={{ disabled: exporting || importingGlobal }}
                    cancelButtonProps={{ disabled: exporting || importingGlobal }}
                >
                    <div className="mb-4">
                        <p className="text-red-500 font-semibold mb-2">{t('central.zipcodes.filters.importWarning')}</p>
                        <p>{t('central.zipcodes.filters.importDescription')}</p>
                    </div>
                    
                    {/* 按钮组 */}
                    <div className="flex flex-col items-end space-y-2">
                        {!exportCompleted && (
                            <Button 
                                type="primary" 
                                className="w-32" 
                                onClick={handleExport}
                                loading={exportLoading || exporting}
                                disabled={exporting || importingGlobal}
                            >
                                {t('central.zipcodes.filters.export')}
                            </Button>
                        )}
                        {exportCompleted && (
                            <Button 
                                type="primary" 
                                className="w-32" 
                                onClick={handleContinueImport} 
                                disabled={exporting || importingGlobal}
                            >
                                {t('central.zipcodes.filters.continueImport')}
                            </Button>
                        )}
                        <Button 
                            className="w-32" 
                            onClick={() => setImportModalVisible(false)} 
                            disabled={exporting || importingGlobal}
                        >
                            {t('central.zipcodes.filters.cancel')}
                        </Button>
                    </div>
                </Modal>

                <input
                    id="zipcode-import-input"
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    disabled={exporting || importingGlobal}
                />

                {/* 确认对话框 */}
                <Modal
                    open={confirmModalVisible}
                    title={t('central.zipcodes.messages.confirmTitle')}
                    onOk={handleConfirm}
                    onCancel={handleCancel}
                    okText={t('central.zipcodes.messages.confirm')}
                    cancelText={t('central.zipcodes.messages.cancel')}
                    confirmLoading={batchUpdateLoading}
                >
                    <p>
                        {pendingAction === 'allow' 
                            ? t('central.zipcodes.messages.confirmAllowTakingOrder')
                            : t('central.zipcodes.messages.confirmRestrictTakingOrder')
                        }
                    </p>
                </Modal>

                {/* 邮编列表 */}
                <ZipcodeColumns
                    zipcodeData={loading ? [] : zipcodeData}
                    loading={loading}
                    rowSelection={isHeadquartersAdmin() ? rowSelection : undefined}
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    paginationProps={{ disabled: exporting || importingGlobal }}
                />
            </div>
        </Spin>
    );
};


export const ZipcodesPage = () => {
    return (
            <PageBody />
    );
};